import { Vue, Component, Prop, Watch, Emit } from 'vue-property-decorator';
import { Mutation } from 'vuex-class';

import TkForm from '@/../bower_components/teknisavuecomponents/components/TkForm.vue';
import Field from '@/../bower_components/teknisavuecomponents/assets/interfaces/Field';
import type { OcorrenciaAprovacao } from '@/assets/ts/controller/OcorrenciaController';
import { OcorrenciaController } from '@/assets/ts/controller/OcorrenciaController';
import SnackbarUtils from '@/../bower_components/teknisavuecomponents/assets/utils/SnackbarUtils';
import Messages from '@/assets/ts/utils/Messages';

interface AprovacaoOcorrenciaMode {
  name: string,
  save: (row: OcorrenciaAprovacao) => Promise<boolean> | boolean,
  condition: (row: OcorrenciaAprovacao) => boolean,
  labels?: { [name: string]: string },
  default?: boolean;
}

@Component({
  name: 'AprovacaoOcorrencia',
  components: {
    TkForm,
  },
})
class AprovacaoOcorrenciaPopup extends Vue {
  // #region [ PROPS ]
  @Prop({ default: false })
  private readonly value: boolean;

  @Prop({ default: () => {} })
  private readonly ocorrencia: OcorrenciaAprovacao;
  // #endregion

  // #region [ STORE ]
  // #endregion

  // #region [ EVENTS ]
  @Emit('after-save')
  private emitAfterSave() {}

  @Emit('close')
  private emitClose() {}

  @Emit('input')
  private emitInput(value: boolean): boolean {
    return value;
  }
  // #endregion

  // #region [ MUTATIONS ]
  // Mutations
  @Mutation('setOcorrencia')
  private setOcorrencia: any;

  // #region [ DATA ]
  private internalValue = false;
  private rowDefaultValue: OcorrenciaAprovacao = {
    NRPROGOCORRENCIA: null,
    NRVINCULOM: null,
    NMVINCULOM: null,
    NRTIPOPROGOCORRENCIA: null,
    NRMOTIVOOCORFREQ: null,
    DTINICIOPROGOCOR: null,
    IDDIASSEMANA: null
  };
  private row: OcorrenciaAprovacao = { ...this.rowDefaultValue }
  private loading = false;
  private extraRowFields = ['CDSTATUS'];

  private modes: AprovacaoOcorrenciaMode[] = [
    {
      name: 'aprovacao',
      labels: {
        save: 'Aprovar',
        title: 'Aprovação de Ocorrência',
      },
      save: this.aprovaOcorrencia,
      condition: (ocorrencia: OcorrenciaAprovacao) => ocorrencia.IDAPROVACAO === 'A',
      default: true,
    },
    {
      name: 'reprovacao',
      labels: {
        save: 'Reprovar',
        title: 'Reprovação de Ocorrência',
      },
      save: this.reprovaOcorrencia,
      condition: (ocorrencia: OcorrenciaAprovacao) => ocorrencia.IDAPROVACAO === 'R',
    },
  ];

  private get fields(): Field[] {
    const fields: Field[] = [
      {
        name: 'NMVINCULOM',
        label: 'Vínculo',
        type: 'text',
        size: 12,
        required: false,
        readonly: true,
      },
      {
        name: 'DTINICIOPROGOCOR',
        label: 'Data Início',
        type: 'text',
        size: 4,
        required: false,
        readonly: true,
      },
      {
        name: 'DIAOCORRE',
        label: 'Dias de Ocorrência',
        type: 'text',
        size: 4,
        required: false,
        readonly: true,
      },
      {
        name: 'DTFIMPROGOCOR',
        label: 'Data Fim',
        type: 'text',
        size: 4,
        required: false,
        readonly: true,
      },
      {
        name: 'NMTIPOPROGOCORRENCIA',
        label: 'Tipo de Ocorrência',
        type: 'text',
        size: 6,
        required: false,
        readonly: true,
      },
      {
        name: 'DSMOTIVOOCORFREQ',
        label: 'Motivo de Ocorrência',
        type: 'text',
        size: 6,
        required: false,
        readonly: true,
      },
      {
        name: 'DSOBSERVACAO',
        label: 'Observação',
        type: 'textarea',
        rows: 2,
        size: 12,
        required: true,
      },
    ];

    return fields;
  }

  private get currentMode() {
    let actualMode: AprovacaoOcorrenciaMode = this.modes.find((mode) => mode.default);
    this.modes.map((mode) => actualMode = mode.condition(this.ocorrencia) ? mode : actualMode);
    return actualMode;
  }

  private get genericFilter() {
    const filter = {
      NRORG: this.$store.getters.user.nrorg,
    };

    return filter;
  }

  private get requestRow(): OcorrenciaAprovacao {
    return this.buildExtraRowMapped();
  }
  // #endregion

  // #region [ WATCHERS ]
  @Watch('value', { immediate: true })
  private valueOnChange() {
    this.internalValue = this.value;

    this.row = { ...this.ocorrencia };
  }
  // #endregion

  // #region [ METHODS ]
  private closePopup() {
    this.internalValue = false;

    this.clearRow();
    this.emitInput(this.internalValue);
  }

  private closePopupOnSuccess() {
    this.closePopup();
    this.emitClose();
  }

  private async save() {
    if (!(this.row as any).validate()) {
      SnackbarUtils.showMessage(Messages.REQUIRED_FIELDS_NOT_FILLED, SnackbarUtils.TYPE_ALERT);
      return;
    }

    this.loading = true;

    try {
      const saveSuccess = await this.currentMode.save(this.requestRow);
      if (saveSuccess) {
        this.closePopupOnSuccess();
      }
    } finally {
      this.loading = false;
    }
  }

  private async aprovaOcorrencia(row: OcorrenciaAprovacao): Promise<boolean> {
    try {
      const nrprogocorrencia = row.NRPROGOCORRENCIA;

      const response = await OcorrenciaController.aprovaOcorrencia(nrprogocorrencia);

      return true;
    } catch (err) {
      const errResponse = (err as any).response;
      const message = errResponse && errResponse.data ? errResponse.data.error : Messages.SOLI_APROV_ERROR;
      SnackbarUtils.showMessage(message, SnackbarUtils.TYPE_ERROR);
    }

    return false;
  }

  private async reprovaOcorrencia(row: OcorrenciaAprovacao): Promise<boolean> {
    try {
      const nrprogocorrencia = row.NRPROGOCORRENCIA;
      const dsObservacao = row.DSOBSERVACAO;

      const response = await OcorrenciaController.rejeitaOcorrencia(nrprogocorrencia, dsObservacao);
      
      return true;
    } catch (err) {
      const errResponse = (err as any).response;
      const message = errResponse && errResponse.data ? errResponse.data.error : Messages.SOLI_REPROV_ERROR;
      SnackbarUtils.showMessage(message, SnackbarUtils.TYPE_ERROR);
    }

    return false;
  }

  private clearRow() {
    this.row = { ...this.rowDefaultValue };
  }

  private buildExtraRowMapped(): OcorrenciaAprovacao {
    const extraRow = {};
    const rowCopy: OcorrenciaAprovacao = { ...this.row };

    this.extraRowFields.map((column) => {
      extraRow[column] = rowCopy[column];
      delete rowCopy[column];
    });

    const row: OcorrenciaAprovacao = { ...rowCopy, EXTRA_ROW: { ...extraRow } };
    return row;
  }

  // #endregion
}

export default AprovacaoOcorrenciaPopup;
